import { FC } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { TabsList, TabsTrigger } from "src/components/ui/tabs";
import { Image, Video, Layers, Maximize } from "lucide-react";
import type { ReactNode } from "react";

interface Props {
	radioOptions: BannerType[];
	showVariantTab?: boolean;
}

const bannerTypeConfig: Record<string, { icon: ReactNode; label: string }> = {
	singleBanner: { icon: <Image className="h-4 w-4" />, label: "Image" },
	fullImageBanner: {
		icon: <Maximize className="h-4 w-4" />,
		label: "Full image"
	},
	fullScreenTakeoverBanner: {
		icon: <Maximize className="h-4 w-4" />,
		label: "Full screen"
	},
	videoBanner: { icon: <Video className="h-4 w-4" />, label: "Video" },
	promotionImage: { icon: <Image className="h-4 w-4" />, label: "Image" },
	promotionVideo: { icon: <Video className="h-4 w-4" />, label: "Video" },
	socialFeedAdvert: {
		icon: <Layers className="h-4 w-4" />,
		label: "Feed advert"
	}
};

const BannerTypePicker: FC<Props> = ({
	radioOptions,
	showVariantTab = true
}) => {
	const { control } = useFormContext<BannerFormState>();

	const { field } = useController({
		name: "bannerType",
		control
	});

	const allFields = useWatch({ control });

	return (
		<div className="mb-6" aria-label="banner-type-picker">
			{showVariantTab && (
				<div className="mb-4">
					<TabsList className="w-fit">
						<TabsTrigger value="standard">Standard</TabsTrigger>
						<TabsTrigger value="campaign">Campaign</TabsTrigger>
					</TabsList>
				</div>
			)}

			{radioOptions.length > 1 && (
				<fieldset>
					<legend className="sr-only">Banner type</legend>

					<div className="inline-flex w-full gap-1 p-1.5 bg-white border border-border rounded-xl shadow-xs">
						{radioOptions.map((value) => {
							const isChecked = allFields.bannerType === value;
							const config = bannerTypeConfig[value] || {
								icon: null,
								label: value
							};

							return (
								<label key={value} className="flex-1 cursor-pointer">
									<input
										type="radio"
										className="sr-only"
										value={value}
										checked={isChecked}
										onChange={() => field.onChange(value)}
									/>
									<div
										className={[
											"flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
											isChecked
												? "bg-primary text-white shadow-md shadow-primary/50"
												: "text-muted-foreground hover:text-foreground hover:bg-white/80"
										].join(" ")}
									>
										{config.icon}
										<span>{config.label}</span>
									</div>
								</label>
							);
						})}
					</div>
				</fieldset>
			)}
		</div>
	);
};

export default BannerTypePicker;
